import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { trackAuditCompleted } from "../lib/analytics";
import { usePageSEO } from "../lib/seo";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const [auditId, setAuditId] = useState(null);
  const [generating, setGenerating] = useState(false);
  usePageSEO({ title: "Payment", noIndex: true });

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payment/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setAuditId(data.audit_id);
          setStatus("paid");
          // Generate report
          setGenerating(true);
          try {
            await api.post(`/report/generate/${data.audit_id}`);
            trackAuditCompleted(null, null);
          } catch (e) {
            console.error("Report generation error:", e);
          }
          setGenerating(false);
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        // Keep polling
        attempts++;
        if (attempts < 10) {
          setTimeout(poll, 2000);
        } else {
          setStatus("failed");
        }
      } catch (err) {
        console.error(err);
        attempts++;
        if (attempts < 10) setTimeout(poll, 2000);
        else setStatus("failed");
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <img src="/stansbury-logo.png" alt="Stansbury AI" className="h-10 mx-auto mb-8" />

        {status === "checking" && (
          <div>
            <Loader2 className="w-10 h-10 animate-spin text-[#0033CC] mx-auto mb-4" />
            <h2 className="text-xl font-['Outfit'] font-medium text-[#0F172A] mb-2">Confirming Your Payment</h2>
            <p className="text-sm text-slate-500 font-['IBM_Plex_Sans']">Please wait while we verify your payment with Stripe...</p>
          </div>
        )}

        {status === "paid" && !generating && (
          <div>
            <div className="w-16 h-16 bg-emerald-50 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-['Outfit'] font-medium text-[#0F172A] mb-2">Payment Successful</h2>
            <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-6">Your AI Growth Report has been generated and is ready to view.</p>
            <Button onClick={() => navigate(auditId ? `/results/${auditId}` : "/dashboard")} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none px-8 h-11 font-['Outfit'] font-medium" data-testid="view-report-btn">
              View Your Report
            </Button>
          </div>
        )}

        {status === "paid" && generating && (
          <div>
            <Loader2 className="w-10 h-10 animate-spin text-[#0033CC] mx-auto mb-4" />
            <h2 className="text-xl font-['Outfit'] font-medium text-[#0F172A] mb-2">Payment Confirmed!</h2>
            <p className="text-sm text-slate-500 font-['IBM_Plex_Sans']">Generating your AI Growth Report...</p>
          </div>
        )}

        {status === "failed" && (
          <div>
            <div className="w-16 h-16 bg-red-50 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-['Outfit'] font-medium text-[#0F172A] mb-2">Payment Issue</h2>
            <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-6">We couldn't confirm your payment. Please try again or contact support.</p>
            <Button onClick={() => navigate("/dashboard")} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none px-8 h-11 font-['Outfit'] font-medium">
              Return to Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
